import { useMotionDisabled } from "@gladis/ui/motion";
import { useEffect, useRef, useState, type ComponentType, type ReactNode } from "react";

import type { AnimatedIconName } from "./animated-icons.js";

/**
 * The slice of lottie-web's instance this component uses.
 *
 * Declared rather than imported: the player's types come with the module, and importing them statically
 * would defeat the dynamic `import()` that keeps 150KB off the first-paint path.
 */
interface Player {
  setDirection: (direction: 1 | -1) => void;
  goToAndPlay: (value: number, isFrame?: boolean) => void;
  goToAndStop: (value: number, isFrame?: boolean) => void;
}

/**
 * A rail icon: a Lottie animation that plays on hover, focus or click, over a static fallback.
 *
 * ==========================================================================================
 * THE HEROICON IS NOT A PLACEHOLDER — IT IS THE ANSWER FOR TWO REAL CASES
 * ==========================================================================================
 *
 * It shows **until the player has loaded**, so the rail is never blank and never shifts: the Lottie
 * replaces it in place, at the same size, once its chunk has arrived.
 *
 * And it shows **for good when motion is disabled**, in which case nothing is fetched at all — no
 * player, no JSON. That is the deliberate part. `SheenSurface` renders a static finish under reduced
 * motion because the surface still has a job without the movement; a Lottie icon frozen at frame 0 is
 * just a different icon set, and downloading a renderer to display an icon we already have is waste.
 * Someone who asked for less motion gets the heroicons, and the two sets are each internally
 * consistent.
 *
 * It also means the e2e suite never loads Lottie — `useMotionDisabled()` is true under
 * `navigator.webdriver` — so the rail stays deterministic and fast in Playwright, and every existing
 * selector keeps working because the accessible name is on the pill, not the icon.
 *
 * ==========================================================================================
 * HOW THE COLOUR WORKS, WHICH IS THE ONE THING THAT LOOKED HARD AND IS NOT
 * ==========================================================================================
 *
 * Every colour in all nine files is the same `.primary` — `rgb(18, 19, 49)`, near-black, and
 * invisible on this app's dark shell. The SVG renderer writes it with `setAttribute('fill', …)` and
 * `setAttribute('stroke', …)`, and a presentation attribute loses to any CSS rule — so
 * `[&_svg_path]:fill-current [&_svg_path]:stroke-current` recolours all of it with no `!important`,
 * no rewriting of the JSON, and no per-icon work.
 *
 * Forcing a fill on stroke-only shapes would normally turn outlines into blobs. It does not here:
 * lottie-web gives a stroke path `fill-opacity="0"`, so its fill is invisible whatever colour it is.
 * Verified in the player's source rather than assumed, because the failure would have been silent on
 * the two icons that are mostly strokes.
 *
 * Following `currentColor` also means the rail's existing states keep working untouched — the pill
 * dims to 30% opacity when idle and the icon dims with it, exactly as the heroicon did.
 */

export function NavIcon({
  name,
  fallback: Fallback,
  playing,
  className,
}: {
  name: AnimatedIconName;
  /** Shown until the player loads, and for good when motion is disabled. */
  fallback: ComponentType<{ className?: string }>;
  /** True while the pill is hovered or focused. Each rising edge restarts the animation. */
  playing: boolean;
  /**
   * REQUIRED, not optional, and `exactOptionalPropertyTypes` is why.
   *
   * The fallback heroicon's own prop is `className?: string`, so forwarding a `string | undefined` to it
   * is a type error — and defaulting it here would put the rail's icon size in two places. Both callers
   * pass `NAV_ICON`; there is no case where a rail icon has no size.
   */
  className: string;
}): ReactNode {
  const host = useRef<HTMLSpanElement | null>(null);
  const player = useRef<Player | null>(null);
  const [ready, setReady] = useState(false);
  const motionDisabled = useMotionDisabled();

  useEffect(() => {
    // Nothing is fetched when motion is disabled. See the header — this is the requirement.
    if (motionDisabled) return;

    const node = host.current;
    if (!node) return;

    let animation: (Player & { destroy: () => void }) | null = null;
    let cancelled = false;

    void (async () => {
      /*
       * `lottie_light`, not the full player: it drops expressions and filters, which none of these
       * icons use, and it is the smaller half of a 250KB library on the shell's critical path.
       *
       * Both imports are dynamic and awaited together, so the chunk and the animation data arrive in
       * parallel rather than one after the other.
       */
      const [{ default: lottie }, { ANIMATED_ICONS }] = await Promise.all([
        import("lottie-web/build/player/lottie_light.js"),
        import("./animated-icons.js"),
      ]);

      // The effect may have been torn down while those were in flight — a rail that mounts and
      // unmounts quickly (a sign-out, say) would otherwise leave a player attached to a dead node.
      if (cancelled) return;

      animation = lottie.loadAnimation({
        container: node,
        renderer: "svg",
        loop: false,
        // Never on load. The rail would play nine animations at once on every page, which reads as a
        // glitch rather than as a flourish — these are a response to being pointed at.
        autoplay: false,
        animationData: ANIMATED_ICONS[name],
      });

      player.current = animation;
      setReady(true);
    })();

    return () => {
      cancelled = true;
      animation?.destroy();
      player.current = null;
      setReady(false);
    };
  }, [motionDisabled, name]);

  useEffect(() => {
    const animation = player.current;
    if (!animation) return;

    /*
     * PLAYS ONCE ON THE WAY IN. ON THE WAY OUT IT IS RESET, NOT REVERSED.
     *
     * These are morph icons: sixty frames from a resting drawing to a "selected" one, and left at the end an
     * icon is permanently changed by having been pointed at. Something has to happen when the pointer
     * leaves.
     *
     * Reversing was the first answer and it was wrong — a second animation on the way out, so brushing past
     * the rail set nine icons animating twice each. `goToAndStop(0)` puts the icon back with no movement at
     * all: one animation per hover, which is what an icon animation is for.
     *
     * THE COST IS STATED: leaving mid-animation cuts it off rather than letting it finish. Waiting for the
     * `complete` event would smooth that, and would also leave the icon in its morphed state for up to a
     * second after the pointer has gone — which is the same "why is that icon different" problem the reset
     * exists to prevent.
     */
    if (playing) {
      animation.setDirection(1);
      animation.goToAndPlay(0, true);
    } else {
      animation.goToAndStop(0, true);
    }
  }, [playing]);

  return (
    <>
      {/*
        Both are always mounted, and `ready` decides which is visible. Swapping the ELEMENT would
        remount the icon and lose the 34px box the rail's layout depends on; hiding one keeps the
        geometry identical whichever is showing.
      */}
      {ready ? null : <Fallback className={className} />}
      <span
        ref={host}
        aria-hidden="true"
        className={[
          /*
           * `block` IS LOAD-BEARING. This host is a `<span>`, which is `display: inline` — and width and
           * height do not apply to an inline box, so `size-[34px]` was silently ignored and lottie sized its
           * SVG against the 64px pill instead. The icons rendered at nearly twice their intended size and
           * the rail's geometry came from the animation rather than from the layout.
           */
          ready ? `block ${className}` : "hidden",
          // `.lottie-icon` in `app.css` carries the two recolouring rules. Written there rather than as
          // arbitrary variants because they select on the ATTRIBUTES lottie sets, and which attribute is on
          // which path is the difference between recolouring an icon and thickening it.
          "lottie-icon",
        ].join(" ")}
      />
    </>
  );
}
